/**
 * =============================================================================
 * IDS CRM Web - A filled-in check form, read only
 * =============================================================================
 *
 * The answers and photos of one form filled in on a work log (a robot check,
 * a site survey, a van check), shown from the form as it was answered
 * (template_snapshot), so a later change to the template never changes what
 * is shown here.
 *
 * Moved out of the asset page's "Review Check" window so the same display is
 * used there, on the customer Timeline and on the ticket and job Work Logs
 * tabs (Gareth 2026-10-12).
 *
 * Props: check = { template_snapshot, template_answers, attachments,
 * check_completed, template_version }.
 *
 * Shown as recorded:
 *  - sections and fields in their order, archived ones left out;
 *  - a follow-up answer only when its condition was met;
 *  - photos under the step they were taken for; any other photo on the form
 *    under "Other photos", so none is hidden;
 *  - a record kept before forms were stored with their layout: its answers
 *    and photos as a plain list.
 *
 * Workscope forms Phase 1 (Gareth 2026-10-22): a reading shows with its unit
 * and is marked when outside its range, a choice shows its option's words, a
 * date as a date, a signature as the signature, a file as a link; a failed
 * answer is marked and shows the note given; a question the form hid at the
 * time (show-when) stays hidden, and an instruction is not an answer.
 * =============================================================================
 */

import React from 'react';
import { authedFileHref } from '../utils/files';
import { questionsByKey, isQuestionVisible, answerFails, NOTE_SUFFIX } from '../utils/formRules';
import './CheckFormReview.css';

const photoUrl = (att) => {
  if (att?.url?.startsWith('/uploads/')) return authedFileHref(att.url);
  return att?.url || '';
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** '2026-10-01' as '1 Oct 2026', read from its parts so no time zone moves the day. */
const formatDay = (value) => {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(value || ''));
  if (!match) return String(value);
  return [Number(match[3]), MONTHS[Number(match[2]) - 1] || match[2], match[1]].join(' ');
};

const isImage = (att) => (att?.type ? String(att.type).startsWith('image/') : true);

const NOT_ANSWERED = 'Not checked';

const optionLabel = (field, value) => (field.options || [])
  .find(option => String(option.value) === String(value))?.label || String(value);

/** One answer in words, by its question's type. */
const formatAnswer = (field, value) => {
  if (value === '' || value === null || value === undefined || (Array.isArray(value) && value.length === 0)) return NOT_ANSWERED;
  switch (field.field_type) {
    case 'reading':
      return field.unit ? [value, field.unit].join(' ') : String(value);
    case 'choice':
      return optionLabel(field, value);
    case 'multi_choice':
      return (Array.isArray(value) ? value : [value]).map(item => optionLabel(field, item)).join(', ');
    case 'date':
      return formatDay(value);
    default:
      return formatValue(value);
  }
};

const countWords = (field, count) => {
  if (count === 0) return 'None added';
  if (field.field_type === 'photo') return count === 1 ? '1 photo' : count + ' photos';
  return count === 1 ? '1 file' : count + ' files';
};

const formatValue = (value) => {
  if (value === 'true' || value === true) return 'Yes';
  if (value === 'false' || value === false) return 'No';
  if (value === 'na') return 'N/A';
  if (value === '' || value === null || value === undefined) return 'Not checked';
  if (Array.isArray(value)) return value.join(', ');
  if (typeof value === 'object') return JSON.stringify(value);
  return String(value);
};

const valueClass = (field, value) => {
  if (value === '' || value === null || value === undefined) return 'value-unchecked';
  if (answerFails(field, value)) return 'value-fail';
  if (field.field_type === 'reading' || field.field_type === 'choice' || field.field_type === 'multi_choice') return '';
  if (field.fail_value !== undefined && String(value) === String(field.fail_value)) return 'value-fail';
  if (field.pass_value !== undefined && String(value) === String(field.pass_value)) return 'value-pass';
  if (value === 'true') return 'value-pass';
  if (value === 'false') return 'value-fail';
  return '';
};

/** 'cell_guarding_secure' as 'Cell guarding secure', for a record with no layout. */
const plainLabel = (key) => {
  const words = String(key).replace(/[_-]+/g, ' ').trim();
  return words ? `${words.charAt(0).toUpperCase()}${words.slice(1)}` : String(key);
};

function Photos({ photos }) {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="answer-photos">
      {photos.map((att, idx) => (isImage(att) ? (
        <a
          key={att.attachment_id || att.file_id || att.url || idx}
          href={photoUrl(att)}
          target="_blank"
          rel="noopener noreferrer"
          className="answer-photo-thumb"
        >
          <img src={photoUrl(att)} alt={att.name || 'Photo'} />
        </a>
      ) : (
        <a
          key={att.attachment_id || att.file_id || att.url || idx}
          href={photoUrl(att)}
          target="_blank"
          rel="noopener noreferrer"
          className="answer-file-link"
        >
          {att.name || 'File'}
        </a>
      )))}
    </div>
  );
}

function CheckComplete({ isCompleted }) {
  return (
    <div className="robot-check-answers">
      <div className="answer-row">
        <div className="answer-content">
          <span className="answer-label">Check Complete</span>
          <span className={`answer-value ${isCompleted ? 'value-pass' : 'value-fail'}`}>
            {isCompleted ? 'Yes' : 'No'}
          </span>
        </div>
      </div>
    </div>
  );
}

function OtherPhotos({ photos }) {
  if (!photos || photos.length === 0) return null;
  return (
    <div className="template-section">
      <h4 className="template-section-title">Other photos</h4>
      <div className="robot-check-answers">
        <div className="answer-row full-width has-photos">
          <Photos photos={photos} />
        </div>
      </div>
    </div>
  );
}

function CheckFormReview({ check }) {
  // A ticket robot check keeps the whole check it was sent with (its old
  // fixed fields plus template_snapshot and template_answers) as its answers,
  // so the form's own answers are one level down. Read them there, or every
  // question shows "Not checked" (found 2026-10-22, live since Release 14).
  const stored = check?.template_answers || {};
  const nested = stored && typeof stored.template_answers === 'object' && stored.template_answers !== null
    && !Array.isArray(stored.template_answers);
  const answers = nested ? stored.template_answers : stored;
  const schema = check?.template_snapshot || (nested ? stored.template_snapshot : null);
  const attachments = Array.isArray(check?.attachments) ? check.attachments : [];
  const isCompleted = check?.check_completed === true;

  if (!schema || !Array.isArray(schema.sections)) {
    const keys = Object.keys(answers);
    if (keys.length === 0 && attachments.length === 0) {
      return (
        <div className="empty-message">
          No template snapshot available for this check.
        </div>
      );
    }
    // Kept before forms were stored with their layout: the answers as they are.
    return (
      <div className="check-form-review">
        <div className="template-check-metadata">
          <span className="template-note">Recorded before forms were kept with their layout. The answers are shown as they were stored.</span>
        </div>
        {keys.length > 0 && (
          <div className="template-section">
            <div className="robot-check-answers">
              {keys.map(key => (
                <div key={key} className="answer-row">
                  <div className="answer-content">
                    <span className="answer-label">{plainLabel(key)}</span>
                    <span className="answer-value">{formatValue(answers[key])}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        <OtherPhotos photos={attachments} />
        <CheckComplete isCompleted={isCompleted} />
      </div>
    );
  }

  const activeSections = schema.sections
    .filter(section => !section.archived)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  if (activeSections.length === 0) {
    return (
      <div className="empty-message">
        This check has no sections to display.
      </div>
    );
  }

  const stepPhotosOf = (stepKey) => attachments.filter(att => att.step_key === stepKey);
  const byKey = questionsByKey(schema);
  // Every photo shown under a field, so the rest can be listed and none is hidden.
  const shownSteps = new Set();

  const sections = activeSections.map(section => {
    // An instruction is not an answer; a question the form hid stays hidden.
    const activeFields = (section.fields || [])
      .filter(field => !field.archived && field.field_type !== 'instruction' && isQuestionVisible(field, answers, byKey))
      .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

    if (activeFields.length === 0) return null;

    return (
      <div key={section.section_key} className="template-section">
        <h4 className="template-section-title">{section.title}</h4>
        <div className="robot-check-answers">
          {activeFields.map(field => {
            const value = answers[field.field_key];
            const stepKey = `template:${field.field_key}`;
            const stepPhotos = stepPhotosOf(stepKey);
            shownSteps.add(stepKey);
            const hasPhotos = stepPhotos.length > 0;
            const isTextType = field.field_type === 'text' || field.field_type === 'number';
            const fails = answerFails(field, value);
            const note = answers[field.field_key + NOTE_SUFFIX];
            const isEvidenceQuestion = field.field_type === 'photo' || field.field_type === 'file';
            const isSignature = field.field_type === 'signature';
            const wide = isTextType || isSignature || isEvidenceQuestion;
            const shownValue = isEvidenceQuestion ? countWords(field, stepPhotos.length) : formatAnswer(field, value);

            // A follow-up answer shows only when its condition was met.
            let followUpContent = null;
            if (field.follow_up?.field && !field.follow_up.field.archived) {
              const conditionMet = String(value) === String(field.follow_up.condition);
              if (conditionMet) {
                const followUpValue = answers[field.follow_up.field.field_key];
                followUpContent = (
                  <div className="answer-row full-width follow-up-row">
                    <div className="answer-content">
                      <span className="answer-label follow-up-label">
                        <span className="follow-up-arrow">↳</span>
                        {field.follow_up.field.label}
                      </span>
                      <span className="answer-value value-note">
                        {followUpValue || 'No details provided'}
                      </span>
                    </div>
                  </div>
                );
              }
            }

            return (
              <React.Fragment key={field.field_key}>
                <div className={['answer-row', wide ? 'full-width' : '', hasPhotos ? 'has-photos' : ''].filter(Boolean).join(' ')}>
                  <div className="answer-content">
                    <span className="answer-label">
                      {field.label}
                      {fails && <span className="answer-fail-chip">Fail</span>}
                    </span>
                    {isSignature && value && String(value).startsWith('data:image') ? (
                      <img className="answer-signature" src={value} alt={field.label} />
                    ) : (
                      <span className={['answer-value', isEvidenceQuestion ? '' : valueClass(field, value)].filter(Boolean).join(' ')}>
                        {isSignature ? 'Not signed' : shownValue}
                      </span>
                    )}
                  </div>
                  <Photos photos={stepPhotos} />
                </div>
                {fails && note && (
                  <div className="answer-row full-width follow-up-row">
                    <div className="answer-content">
                      <span className="answer-label follow-up-label">
                        <span className="follow-up-arrow">↳</span>
                        What was wrong
                      </span>
                      <span className="answer-value value-note">{note}</span>
                    </div>
                  </div>
                )}
                {followUpContent}
              </React.Fragment>
            );
          })}
        </div>
      </div>
    );
  });

  const otherPhotos = attachments.filter(att => !shownSteps.has(att.step_key));

  return (
    <div className="check-form-review">
      <div className="template-check-metadata">
        <span className="template-badge">
          Template v{check.template_version || '?'}
        </span>
        <span className="template-note">Displayed from saved template snapshot</span>
      </div>

      {sections}

      <OtherPhotos photos={otherPhotos} />

      <CheckComplete isCompleted={isCompleted} />
    </div>
  );
}

export default CheckFormReview;
